import { DateTime, WeekdayNumbers } from 'luxon';
import React, { useEffect, useState } from 'react';

import { LocalizedTime } from '../../helpers/dates';
import { useSendGtmEvent } from '../../hooks/use-send-gtm-event';
import { OpenCloseHoursWeekdays } from './open-close-hours-weekdays.interface';

interface OpeningHoursCompactBlockProps {
  openingHoursArray: OpenCloseHoursWeekdays[];
  node_locale: string;
  timezone: string;
  openingHoursClosed: string;
  exceptionStrings?: string[][];
  exceptionTitle?: string;
}

export const OpeningHoursCompactBlock = ({
  openingHoursArray,
  node_locale,
  timezone,
  openingHoursClosed,
  exceptionStrings,
  exceptionTitle,
}: OpeningHoursCompactBlockProps) => {
  const [openingHoursStrings, setOpeningHoursStrings] = useState<string[][]>();

  useEffect(() => {
    const luxonWeekdayFormatString = 'cccc';
    const titleCaseString = (string: string): string => {
      return string
        .split(' ')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    };

    const reducedHours = openingHoursArray?.reduce<
      Array<OpenCloseHoursWeekdays>
    >((accumulated, current, index) => {
      // index starts at 0, index + 1 represent weekdays 1 - 7
      const weekdayNumber = index + 1;
      const foundIndex = accumulated?.findIndex(
        (hours) => hours.open === current.open && hours.close === current.close,
      );
      if (foundIndex > -1) {
        //Test to see if index + 1 is far away from other days....
        if (
          accumulated[foundIndex].weekDays.includes(weekdayNumber - 1) ||
          accumulated[foundIndex].weekDays.includes(weekdayNumber + 1)
        ) {
          accumulated[foundIndex].weekDays.push(weekdayNumber);
        } else if (
          !accumulated[foundIndex].weekDays.includes(weekdayNumber - 1) ||
          !accumulated[foundIndex].weekDays.includes(weekdayNumber + 1)
        ) {
          accumulated.push(current);
        }
      } else {
        accumulated.push(current);
      }
      return accumulated;
    }, []);

    const hoursStrings: string[][] = reducedHours?.map((openingHours) => {
      const isClosed =
        openingHours.open === 'Closed' || openingHours.close === 'Closed';
      const temporaryDateTime = DateTime.now()
        .setLocale(node_locale)
        .setZone(timezone);

      if (openingHours.weekDays.length > 1) {
        // Ensure fromDay and toDay are of type WeekdayNumbers
        const fromDay: WeekdayNumbers = openingHours
          .weekDays[0] as WeekdayNumbers;
        const toDay: WeekdayNumbers = openingHours.weekDays[
          openingHours.weekDays.length - 1
        ] as WeekdayNumbers;

        // Assuming temporaryDateTime is a Luxon DateTime instance
        const openHoursString = `${titleCaseString(
          temporaryDateTime
            .set({ weekday: fromDay })
            .toFormat(luxonWeekdayFormatString),
        )} - ${titleCaseString(
          temporaryDateTime
            .set({ weekday: toDay })
            .toFormat(luxonWeekdayFormatString),
        )}`;

        const timeString = `${
          !isClosed
            ? `${LocalizedTime(
                openingHours.open,
                node_locale,
              )} - ${LocalizedTime(openingHours.close, node_locale)}`
            : openingHoursClosed
        }`;

        return [openHoursString, timeString];
      } else if (openingHours.weekDays.length === 1) {
        const singleDay: WeekdayNumbers = openingHours
          .weekDays[0] as WeekdayNumbers; // Cast to WeekdayNumbers
        const openHoursString = `${titleCaseString(
          temporaryDateTime
            .set({ weekday: singleDay })
            .toFormat(luxonWeekdayFormatString),
        )}`;
        const timeString = `${
          !isClosed
            ? `${LocalizedTime(
                openingHours.open,
                node_locale,
              )} - ${LocalizedTime(openingHours.close, node_locale)}`
            : openingHoursClosed
        }`;

        return [openHoursString, timeString];
      }

      // If there are no weekdays, you can handle that case here if necessary.

      return [];
    });

    setOpeningHoursStrings(hoursStrings);
  }, [
    setOpeningHoursStrings,
    node_locale,
    openingHoursArray,
    timezone,
    openingHoursClosed,
  ]);

  useSendGtmEvent({ event: 'view_opening_hours' });

  return (
    <>
      <div className="mb-8 flex flex-col">
        {openingHoursStrings?.map((openHourItemArray, index) => (
          <div
            className="mb-1 flex justify-between"
            key={`open-hours-${index}`}
          >
            <div className="w-[55%]">{openHourItemArray[0]}</div>
            <div className="ml-2 w-[45%] text-right text-headings">
              {openHourItemArray[1]}
            </div>
          </div>
        ))}
      </div>
      {exceptionStrings && exceptionStrings?.length > 0 && (
        <h2 className="mb-4 font-title text-headings text-text-default">
          {exceptionTitle}
        </h2>
      )}
      {exceptionStrings &&
        exceptionStrings?.map((exception, index) => (
          <div
            className="mb-1 flex justify-between"
            key={`open-hours-${index}`}
          >
            <div className="w-[55%]">{exception[0]}</div>
            <div className="ml-2 w-[45%] text-right text-headings">
              {exception[1]}
            </div>
          </div>
        ))}
    </>
  );
};
